import axios from 'axios';
import store from '../store/index';
import Utils from '../helpers/Utils';
import SecureAPI from '../services/api/SecureAPI';

/**
 * Attempts Cognito auto-login once per load.
 * 1. If there is already a session in Vuex -> do nothing.
 * 2. If no `id_token` cookie exists -> do nothing.
 * 3. POST /api/auto-login (cookies automatically sent). On success:
 *    – commits `SET_SESSION` & `SET_USER` to Vuex so existing code path works.
 *    – removes JWT cookies on the client side as double-insurance.
 *
 * @returns {Promise<boolean>}  true when auto-login succeeded, false otherwise.
 */
let executed = false;
export default async function autoLoginGuard () {
    if (executed) {
        return false; // only once per refresh
    }
    executed = true;

    // 1. Already authenticated?
    const currentSession = store.state.user && store.state.user.session;
    if (!Utils.isEmpty(currentSession)) {
        return false;
    }

    // 2. Always attempt POST; HttpOnly cookies (id_token / refresh_token) will be sent automatically.
    const idToken = Utils.getCookie('id_token'); // may be empty when HttpOnly; used only for email extraction later.

    try {
        const res = await axios.post(`${SecureAPI.getHost()}/api/auto-login`, {}, { withCredentials: true });
        if (res.data && res.data.session_id) {
            store.commit('user/SET_SESSION', res.data.session_id);
            
            // If backend returned user email, commit it directly (works even when id_token is HttpOnly)
            if (res.data.user) {
                store.commit('user/SET_USER', res.data.user);
            }

            // Attempt to retrieve user email from the id_token so that the header can display it
            try {
                const payloadPart = idToken.split('.')[1];
                const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
                const claims = JSON.parse(jsonPayload);
                if (claims && claims.email) {
                    store.commit('user/SET_USER', claims.email);
                }
            } catch (e) {
                console.warn('[AutoLoginGuard] Failed to decode JWT for email extraction', e);
            }

            // Clean up cookies client-side
            Utils.removeCookie('id_token');
            Utils.removeCookie('refresh_token');
            return true;
        }
    } catch (err) {
        // Handle unexpected errors (network issues, 5xx, etc.)
        console.warn('[AutoLoginGuard] Auto-login request failed', err);
    }
    return false;
} 